<template>
  <div data-testid="accruals-activity-panel" class="accruals-activity-panel">
    <div v-if="loading" class="accruals-activity-panel__loading" data-testid="accruals-activity-panel__loading">
      <v-progress-circular indeterminate color="primary" size="32" />
    </div>

    <p
      v-else-if="!groupedEntries.length"
      class="accruals-activity-panel__empty"
      data-testid="accruals-activity-panel__empty"
    >
      No activity recorded yet.
    </p>

    <div
      v-else
      class="accruals-activity-panel__timeline"
      data-testid="accruals-activity-panel__timeline"
    >
      <section
        v-for="(group, groupIndex) in groupedEntries"
        :key="group.dateKey"
        class="accruals-activity-panel__date-group"
        :data-testid="`accruals-activity-panel__date-group-${groupIndex}`"
      >
        <div
          class="accruals-activity-panel__date-label"
          :data-testid="`accruals-activity-panel__date-${groupIndex}`"
        >
          {{ group.dateLabel }}
        </div>

        <div class="accruals-activity-panel__entries">
          <div
            v-if="group.items.length > 1"
            class="accruals-activity-panel__entries-line"
            aria-hidden="true"
          />

          <div
            v-for="(entry, entryIndex) in group.items"
            :key="`${group.dateKey}-${entryIndex}`"
            class="accruals-activity-panel__row"
            :data-testid="`accruals-activity-panel__entry-${groupIndex}-${entryIndex}`"
          >
            <div class="accruals-activity-panel__rail" aria-hidden="true">
              <span class="accruals-activity-panel__avatar">{{ entry.initials }}</span>
            </div>

            <div class="accruals-activity-panel__content">
              <p
                class="accruals-activity-panel__action"
                :data-testid="`accruals-activity-panel__action-${groupIndex}-${entryIndex}`"
              >
                {{ entry.actionLine }}
              </p>
              <p
                class="accruals-activity-panel__timestamp"
                :data-testid="`accruals-activity-panel__timestamp-${groupIndex}-${entryIndex}`"
              >
                {{ entry.timestamp }}
              </p>
              <div
                v-if="entry.detail"
                class="accruals-activity-panel__detail"
                :data-testid="`accruals-activity-panel__detail-${groupIndex}-${entryIndex}`"
              >
                {{ entry.detail }}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { ADMIN_CONSOLE_ACCRUALS_ACTIVITY_MODULE_NAMES } from '@/modules/admin/utils/adminConsoleConstants';

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
}

function parseDetailBlock(log) {
  const meta = log?.meta || {};
  const text = String(log?.text || '').toLowerCase();

  const comment = meta.comment || meta.message || meta.remark || log.comment;
  if (comment) {
    const quoted = String(comment).trim();
    return quoted.startsWith('"') ? quoted : `"${quoted}"`;
  }

  const fileName = meta.file_name || meta.filename || meta.fileName || meta.attachment;
  if (fileName) {
    return `Attached file: ${fileName}`;
  }

  if (text.includes('comment') && (meta.detail || log.detail)) {
    const detail = String(meta.detail || log.detail).trim();
    return detail.startsWith('"') ? detail : `"${detail}"`;
  }

  if ((text.includes('upload') || text.includes('attached') || text.includes('document')) && log.attachment) {
    return `Attached file: ${log.attachment}`;
  }

  return '';
}

export default {
  name: 'AccrualsActivityPanel',
  props: {
    active: { type: Boolean, default: false },
  },
  data() {
    return {
      loading: false,
      loglist: [],
    };
  },
  computed: {
    entries() {
      return this.loglist
        .map((log) => {
          const user = log.userid_detail || {};
          const userName = user.name || user.contactemail || '';
          const createdAt = log.created_at ? new Date(log.created_at) : null;
          const actionLine = log.text || log.type || 'Activity update';
          const detail = parseDetailBlock(log);

          return {
            dateKey: createdAt
              ? this.$nova.formatDateTime(createdAt, 'YYYY-MM-DD')
              : 'unknown',
            dateLabel: createdAt
              ? this.$nova.formatDateTime(createdAt, 'DD MMM YYYY').toUpperCase()
              : 'UNKNOWN DATE',
            timestamp: createdAt
              ? this.$nova.formatDateTime(createdAt, 'DD MMM YYYY · HH:mm')
              : '—',
            actionLine,
            detail,
            initials: getInitials(userName),
            sortTime: createdAt ? createdAt.getTime() : 0,
          };
        })
        .sort((a, b) => b.sortTime - a.sortTime);
    },
    groupedEntries() {
      const groups = new Map();

      this.entries.forEach((entry) => {
        if (!groups.has(entry.dateKey)) {
          groups.set(entry.dateKey, {
            dateKey: entry.dateKey,
            dateLabel: entry.dateLabel,
            items: [],
          });
        }
        groups.get(entry.dateKey).items.push(entry);
      });

      return Array.from(groups.values()).sort((a, b) => {
        if (a.dateKey === 'unknown') return 1;
        if (b.dateKey === 'unknown') return -1;
        return b.dateKey.localeCompare(a.dateKey);
      });
    },
  },
  watch: {
    active(isActive) {
      if (isActive) {
        this.loadActivity();
      }
    },
  },
  mounted() {
    if (this.active) {
      this.loadActivity();
    }
  },
  methods: {
    async loadActivity() {
      this.loading = true;
      this.loglist = [];
      try {
        const results = await Promise.all(
          ADMIN_CONSOLE_ACCRUALS_ACTIVITY_MODULE_NAMES.map((moduleName) =>
            this.$store
              .dispatch('fetchActivityLog', {
                consolidated: true,
                moduleName,
                type: [],
              })
              .catch(() => ({ data: [] }))
          )
        );

        const merged = results.flatMap((result) => result?.data || []);
        const byId = new Map();
        merged.forEach((log) => {
          const id = log?._id ? String(log._id) : '';
          if (id) {
            byId.set(id, log);
            return;
          }
          byId.set(`${log?.created_at || ''}-${log?.text || ''}-${byId.size}`, log);
        });

        this.loglist = Array.from(byId.values()).sort(
          (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
        );
      } catch (err) {
        console.log('[AccrualsActivityPanel]: loadActivity', err);
        this.loglist = [];
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped src="./AccrualsActivityPanel.scss"></style>
